import { useState, useCallback, useMemo, useEffect } from "react";
import { showToastNotification } from "@/utility/helper";
import type { ISalaryPreviewUser } from "@/interface/attendance.interface";

export interface ISalaryPreviewDraftResult {
  draftStorageKey: string;
  draftEdits: Record<string, Record<string, number>>;
  isDraftActive: boolean;
  draftTimestamp: string | null;
  setDraftEdits: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, number>>>
  >;
  setIsDraftActive: React.Dispatch<React.SetStateAction<boolean>>;
  setDraftTimestamp: React.Dispatch<React.SetStateAction<string | null>>;
  loadDraftFromStorage: () => Record<string, Record<string, number>> | null;
  saveDraftToStorage: (
    newEdits: Record<string, Record<string, number>>
  ) => void;
  handleClearDraft: () => void;
  applyDraftAndRecalculateUsers: (
    users: ISalaryPreviewUser[],
    draftMap: Record<string, Record<string, number>>
  ) => ISalaryPreviewUser[];
}

interface UseSalaryPreviewDraftParams {
  siteId?: string;
  userSiteId?: string;
  startDate: string;
  endDate: string;
}

export function useSalaryPreviewDraft({
  siteId,
  userSiteId,
  startDate,
  endDate,
}: UseSalaryPreviewDraftParams): ISalaryPreviewDraftResult {
  const draftStorageKey = useMemo(() => {
    const currentSite = siteId || userSiteId || "all";
    return `salary_preview_draft_${currentSite}_${startDate}_${endDate}`;
  }, [siteId, userSiteId, startDate, endDate]);

  const [draftEdits, setDraftEdits] = useState<
    Record<string, Record<string, number>>
  >({});
  const [isDraftActive, setIsDraftActive] = useState<boolean>(false);
  const [draftTimestamp, setDraftTimestamp] = useState<string | null>(null);

  // Load draft from localStorage
  const loadDraftFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(draftStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.userEdits && Object.keys(parsed.userEdits).length > 0) {
          setDraftEdits(parsed.userEdits);
          setIsDraftActive(true);
          setDraftTimestamp(parsed.updatedAt || new Date().toISOString());
          return parsed.userEdits as Record<string, Record<string, number>>;
        }
      }
    } catch (e) {
      console.error("Failed to parse salary draft:", e);
    }
    setDraftEdits({});
    setIsDraftActive(false);
    setDraftTimestamp(null);
    return null;
  }, [draftStorageKey]);

  // Save draft to localStorage
  const saveDraftToStorage = useCallback(
    (newEdits: Record<string, Record<string, number>>) => {
      try {
        const timestamp = new Date().toISOString();
        localStorage.setItem(
          draftStorageKey,
          JSON.stringify({ updatedAt: timestamp, userEdits: newEdits })
        );
        setDraftEdits(newEdits);
        setIsDraftActive(true);
        setDraftTimestamp(timestamp);
      } catch (e) {
        console.error("Failed to save salary draft:", e);
      }
    },
    [draftStorageKey]
  );

  // Clear draft and reset
  const handleClearDraft = useCallback(() => {
    try {
      localStorage.removeItem(draftStorageKey);
    } catch (e) {
      console.error("Failed to clear salary draft:", e);
    }
    setDraftEdits({});
    setIsDraftActive(false);
    setDraftTimestamp(null);
    showToastNotification("info", "Draft cleared. Reset to original preview data.");
  }, [draftStorageKey]);

  /**
   * Merges draft edits into userSalary items and recalculates
   * Gross Salary & Net Salary based on updated earning/deduction head values.
   */
  const applyDraftAndRecalculateUsers = useCallback(
    (
      users: ISalaryPreviewUser[],
      draftMap: Record<string, Record<string, number>>
    ): ISalaryPreviewUser[] => {
      return users.map((u) => {
        const userId = u.user?.id;
        if (!userId) return u;

        const userDraft = draftMap[userId];
        const initialSalaryList = u.userSalary || [];

        // Step 1: Merge draft values into salary items
        const mergedSalaryList = initialSalaryList.map((sal) => {
          const headId = sal.branchSalaryHeadId || sal.id || "";
          const headName = sal.salaryName || "";
          let updatedAmount = sal.amount ?? 0;

          if (userDraft) {
            if (headId && userDraft[headId] !== undefined) {
              updatedAmount = Number(userDraft[headId]) || 0;
            } else if (headName && userDraft[headName] !== undefined) {
              updatedAmount = Number(userDraft[headName]) || 0;
            }
          }
          return { ...sal, amount: updatedAmount };
        });

        // Step 2: Tally earnings & deductions (skip Gross/Net head rows)
        const isGrossHead = (name: string) =>
          ["gross salary", "gross-salary", "gross_salary"].includes(
            name.toLowerCase().trim()
          );
        const isNetHead = (name: string) => {
          const n = name.toLowerCase().trim();
          return (
            n.includes("net salary") ||
            n.includes("net-salary") ||
            n === "net pay"
          );
        };

        let calcEarning = 0;
        let calcDeduction = 0;

        mergedSalaryList.forEach((sal) => {
          const name = sal.salaryName || "";
          if (!isGrossHead(name) && !isNetHead(name)) {
            if (sal.type === "earning") calcEarning += Number(sal.amount) || 0;
            else if (sal.type === "deduction")
              calcDeduction += Number(sal.amount) || 0;
          }
        });

        const calcNetSalary = calcEarning - calcDeduction;

        // Step 3: Update Gross & Net rows in the salary list
        const finalSalaryList = mergedSalaryList.map((sal) => {
          const name = sal.salaryName || "";
          if (isGrossHead(name)) return { ...sal, amount: calcEarning };
          if (isNetHead(name)) return { ...sal, amount: calcNetSalary };
          return sal;
        });

        return {
          ...u,
          userSalary: finalSalaryList,
          totals: {
            earning: calcEarning,
            deduction: calcDeduction,
            netSalary: calcNetSalary,
          },
        };
      });
    },
    []
  );

  // Browser navigation guard when draft has unsaved edits
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDraftActive) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDraftActive]);

  return {
    draftStorageKey,
    draftEdits,
    isDraftActive,
    draftTimestamp,
    setDraftEdits,
    setIsDraftActive,
    setDraftTimestamp,
    loadDraftFromStorage,
    saveDraftToStorage,
    handleClearDraft,
    applyDraftAndRecalculateUsers,
  };
}
